/**
 * Notification Integration Service
 * Automatically creates notifications when events occur
 */

import NotificationService from '../services/notificationService';
import { calendarService } from './calendarService';
import { logger } from './logger';

const log = logger.createScope('NotificationIntegrationService');

class NotificationIntegrationService {
  /**
   * Initialize notification listeners for all services
   */
  static initialize(userId: string) {
    log.info('Initializing notification integration', { userId });
    
    // Set up document processing notifications
    this.setupDocumentNotifications(userId);
    
    // Set up appointment notifications
    this.setupAppointmentNotifications(userId);
    
    // Set up reminder notifications
    this.setupReminderNotifications(userId);
    
    // Set up periodic cleanup
    this.setupPeriodicCleanup();
  }

  /**
   * Set up notifications for document processing events
   */
  private static setupDocumentNotifications(userId: string) {
    // Listen for document processing events
    document.addEventListener('documentProcessed', (event: Event) => {
      const { petId, petName, documentType, hasAbnormalValues, documentId, labName } = (event as CustomEvent).detail;
      
      NotificationService.notifyLabResultProcessed(
        userId,
        petId,
        petName,
        documentType,
        hasAbnormalValues,
        documentId,
        labName
      ).catch(error => {
        log.error('Error creating lab result notification', error as Error, { userId, petId, documentId });
      });
    });

    // Listen for review requests
    document.addEventListener('reviewRequested', (event: Event) => {
      const { petId, petName, documentType, documentId } = (event as CustomEvent).detail;
      
      NotificationService.notifyReviewRequest(
        userId,
        petId,
        petName,
        { documentType, documentId }
      ).catch(error => {
        log.error('Error creating review request notification', error as Error, { userId, petId, documentId });
      });
    });

    // Listen for vet visit summaries
    document.addEventListener('vetVisitProcessed', (event: Event) => {
      const { petId, petName, visitData } = (event as CustomEvent).detail;
      
      NotificationService.notifyVetVisitSummary(
        userId,
        petId,
        petName,
        visitData
      ).catch(error => {
        log.error('Error creating vet visit notification', error as Error, { userId, petId });
      });
    });
  }

  /**
   * Set up notifications for appointment events
   */
  private static setupAppointmentNotifications(userId: string) {
    // Listen for new appointments
    document.addEventListener('appointmentCreated', (event: Event) => {
      const { petId, petName, appointmentData } = (event as CustomEvent).detail;
      
      NotificationService.notifyAppointmentCreated(
        userId,
        petId,
        petName,
        appointmentData
      ).catch(error => {
        log.error('Error creating appointment notification', error as Error, { userId, petId });
      });
    });

    // Check for upcoming appointments every hour
    setInterval(async () => {
      try {
        const upcomingEvents = await calendarService.getUpcomingEvents(userId);
        const now = new Date();
        
        for (const event of upcomingEvents) {
          const eventTime = new Date(event.startTime);
          const hoursUntil = (eventTime.getTime() - now.getTime()) / (1000 * 60 * 60);
          
          // Notify if appointment is within 24 hours and we haven't notified recently
          if (hoursUntil > 0 && hoursUntil <= 24) {
            const lastNotificationKey = `appointment_${event.id}_${Math.floor(hoursUntil)}`;
            const lastNotified = localStorage.getItem(lastNotificationKey);
            
            if (!lastNotified) {
              await NotificationService.notifyUpcomingAppointment(
                userId,
                event.petId,
                'Your pet', // Default pet name since CalendarEvent doesn't have petName
                event,
                Math.floor(hoursUntil)
              );
              
              localStorage.setItem(lastNotificationKey, 'true');
            }
          }
        }
      } catch (error) {
        log.error('Error checking upcoming appointments', error as Error, { userId });
      }
    }, 60 * 60 * 1000); // Check every hour
  }

  /**
   * Set up notifications for reminder events
   */
  private static setupReminderNotifications(userId: string) {
    // Listen for medication reminders
    document.addEventListener('medicationReminder', (event: Event) => {
      const { petId, petName, medicationData } = (event as CustomEvent).detail;
      
      NotificationService.notifyMedicationReminder(
        userId,
        petId,
        petName,
        medicationData
      ).catch(error => {
        log.error('Error creating medication notification', error as Error, { userId, petId });
      });
    });

    // Listen for vaccination reminders
    document.addEventListener('vaccinationDue', (event: Event) => {
      const { petId, petName, vaccinationData } = (event as CustomEvent).detail;
      
      NotificationService.notifyVaccinationDue(
        userId,
        petId,
        petName,
        vaccinationData
      ).catch(error => {
        log.error('Error creating vaccination notification', error as Error, { userId, petId });
      });
    });
  }

  /**
   * Set up periodic cleanup of expired notifications
   */
  private static setupPeriodicCleanup() {
    // Clean up expired notifications every 6 hours
    setInterval(() => {
      NotificationService.cleanupExpiredNotifications().catch(error => {
        log.error('Error cleaning up expired notifications', error as Error);
      });
    }, 6 * 60 * 60 * 1000);
  }

  /**
   * Create a test notification (for development)
   */
  static async createTestNotification(userId: string, type: string = 'lab_result') {
  const testData = {
    lab_result: {
      title: '🧪 Lab Results Ready - Buddy',
      message: 'Lab results for Buddy have been processed. Some values may need your attention.',
      petName: 'Buddy',
      petId: 'test-pet-id',
      hasAbnormalValues: true
    },
    appointment: {
      title: '📅 Appointment Tomorrow - Luna',
      message: 'Appointment for Luna is tomorrow at 2:30 PM.',
      petName: 'Luna',
      petId: 'test-pet-id-2',
      appointmentData: { startTime: new Date(Date.now() + 24 * 60 * 60 * 1000) }
    },
    medication: {
      title: '💊 Medication Reminder - Max',
      message: 'Time to give Max their heart medication.',
      petName: 'Max',
      petId: 'test-pet-id-3',
      medicationData: { medicationName: 'Heart Medication' }
    },
    vet_visit: {
      title: '🏥 Vet Visit Summary - Charlie',
      message: 'Visit summary for Charlie has been processed. Review the details and any follow-up recommendations.',
      petName: 'Charlie',
      petId: 'test-pet-id-4',
      visitData: { visitDate: new Date(), summary: 'Annual checkup completed' }
    }
  };

  const data = testData[type as keyof typeof testData];
  if (!data) return;

  try {
    switch (type) {
      case 'lab_result':
        await NotificationService.notifyLabResultProcessed(
          userId,
          data.petId,
          data.petName,
          'Blood Test',
          ('hasAbnormalValues' in data ? (data as { hasAbnormalValues: boolean }).hasAbnormalValues : false),
          'test-doc-id'
        );
        break;
      case 'appointment':
        await NotificationService.notifyUpcomingAppointment(
          userId,
          data.petId,
          data.petName,
          ('appointmentData' in data ? (data as { appointmentData: { startTime: Date } }).appointmentData : { startTime: new Date() }),
          24
        );
        break;
      case 'medication':
        await NotificationService.notifyMedicationReminder(
          userId,
          data.petId,
          data.petName,
          ('medicationData' in data ? (data as { medicationData: { medicationName: string } }).medicationData : { medicationName: '' })
        );
        break;
      case 'vet_visit':
        await NotificationService.notifyVetVisitSummary(
          userId,
          data.petId,
          data.petName,
          ('visitData' in data ? (data as { visitData: { visitDate: Date; summary: string } }).visitData : { visitDate: new Date(), summary: '' })
        );
        break;
    }
      
      log.info('Test notification created', { userId, type });
    } catch (error) {
      log.error('Error creating test notification', error as Error, { userId, type });
    }
  }

  /**
   * Dispatch events for testing (development only)
   * 
   * ⚠️ WARNING: This creates test notifications in Firestore!
   * Only call this manually via NotificationTest component, NOT automatically on app load.
   * 
   * DISABLED: Auto-dispatch was removed from App.tsx to prevent creating hundreds of test notifications.
   */
  static dispatchTestEvents() {
    log.warn('⚠️ dispatchTestEvents called - this creates test notifications in Firestore!');
    log.debug('Dispatching test notification events');
    
    // Test lab result notification
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('documentProcessed', {
        detail: {
          petId: 'test-pet-1',
          petName: 'Buddy',
          documentType: 'Blood Test',
          hasAbnormalValues: true,
          documentId: 'test-doc-1'
        }
      }));
    }, 1000);

    // Test appointment notification
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('appointmentCreated', {
        detail: {
          petId: 'test-pet-2',
          petName: 'Luna',
          appointmentData: {
            id: 'test-appt-1',
            startTime: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
            title: 'Annual Checkup'
          }
        }
      }));
    }, 2000);

    // Test medication reminder
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('medicationReminder', {
        detail: {
          petId: 'test-pet-3',
          petName: 'Max',
          medicationData: {
            medicationName: 'Heart Medication',
            dosage: '5mg',
            frequency: 'Twice daily'
          }
        }
      }));
    }, 3000);

    // Test vet visit summary
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('vetVisitProcessed', {
        detail: {
          petId: 'test-pet-4',
          petName: 'Charlie',
          visitData: {
            visitDate: new Date(),
            summary: 'Annual checkup completed',
            recommendations: 'Continue current diet and exercise routine'
          }
        }
      }));
    }, 4000);
  }
}

export default NotificationIntegrationService;
